import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

/**
 * Bridges the "Layout preference" entry in the header ProfileMenu (App subtree)
 * to the actual dialog, which is rendered inside MainPageLayout (Outlet subtree)
 * next to the resizable PanelGroup it controls. Only the open/close signal
 * crosses component trees here — the pane ref stays local to MainPageLayout.
 */
interface LayoutPreferenceContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

const LayoutPreferenceContext = createContext<LayoutPreferenceContextValue>({
  isOpen: false,
  open: () => {},
  close: () => {},
});

export const LayoutPreferenceProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const value = useMemo(() => ({ isOpen, open, close }), [isOpen, open, close]);

  return (
    <LayoutPreferenceContext.Provider value={value}>
      {children}
    </LayoutPreferenceContext.Provider>
  );
};

export const useLayoutPreference = () => useContext(LayoutPreferenceContext);
